import first from "lodash/first";
import last from "lodash/last";
import size from "lodash/size";
import moment from "moment-timezone";
import { CurrencyCodeEnum, TripCategory } from "types";
import { TimeFormatOptions } from "globals/hooks/useTimeFormat";

export function currency(
  money: number,
  currencyCode: CurrencyCodeEnum = CurrencyCodeEnum.Usd
) {
  if (money === undefined) return "-";
  // To add currency support, add a new currency code to local entry here.
  const localMap = {
    [CurrencyCodeEnum.Usd]: "en-US",
    [CurrencyCodeEnum.Cad]: "en-CA",
    [CurrencyCodeEnum.Uah]: "uk-UA", // Ukraine
    [CurrencyCodeEnum.Eur]: "nl-NL", // Netherlands
    [CurrencyCodeEnum.Aud]: "en-AU", // Australia
    [CurrencyCodeEnum.Dkk]: "da-DK", // Denmark
    [CurrencyCodeEnum.Nzd]: "en-NZ", // New Zealand
    [CurrencyCodeEnum.Chf]: "de-CH", // Switzerland
  };

  return new Intl.NumberFormat(localMap[currencyCode], {
    style: "currency",
    currency: currencyCode.toUpperCase(),
  }).format(money);
}

export function convertMinutesToHoursMinutes(
  inputMinutes: number,
  formal?: boolean
) {
  const hours = Math.floor(inputMinutes / 60);
  const minutes = Math.round(inputMinutes % 60);

  let result = "";

  if (formal) {
    if (hours) {
      result += `${hours} Hour${hours > 1 ? "s" : ""}`;
    }
    if (minutes) {
      result += ` ${minutes} Minute${minutes > 1 ? "s" : ""}`;
    }
  } else {
    if (hours) {
      result += `${hours}h`;
    }
    if (minutes) {
      result += ` ${minutes}min`;
    }
  }

  return result || "N/A";
}

export const parseNumberFromFlightNumber = (ident: string): string => {
  if (!ident) return "";
  return ident.replace(/([^0-9])/g, "");
};

export const isValidPhoneNumber = (phoneNumber: string) => {
  const unmaskedPhone = phoneNumber
    .toString()
    .replace(/[+]/, "")
    .replace(/^1/, "")
    .replace(/\s/g, "")
    .replace(/[()]/g, "")
    .replace(/-/g, "");

  return unmaskedPhone.length === 10;
};

/**
 * get total duration of a trips using moment difference
 * time between first and last stop dateTime
 * @param stops array of stops on a trip
 */
export const calculateTotalDurationMinutes = (stops: any[]): number => {
  const startDateTime = stops[0].dateTime;
  const endDateTime = stops[stops.length - 1].dateTime;

  // do not calculate if missing fields
  if (!startDateTime || !endDateTime) return null;

  return Math.floor(
    moment.duration(moment(endDateTime).diff(moment(startDateTime))).asMinutes()
  );
};

const orderTripsByAscendingStopTimes = (
  trips: {
    tripCategory?: TripCategory;
    charterServiceDate?: string;
    stops: { dateTime?: string }[];
  }[]
) => {
  if (size(trips) < 2) return trips;

  return trips.slice().sort((tripA, tripB) => {
    const tripADate =
      tripA.tripCategory === TripCategory.Charter
        ? tripA.charterServiceDate
        : first(tripA.stops)?.dateTime;
    const tripBDate =
      tripB.tripCategory === TripCategory.Charter
        ? tripB.charterServiceDate
        : first(tripB.stops)?.dateTime;

    return moment(tripADate).diff(moment(tripBDate));
  });
};

export const calculateRangeOfTripDates = (
  trips: {
    tripCategory?: TripCategory;
    charterServiceDate?: string;
    stops: { dateTime?: string }[];
  }[],
  formatTime?: (date: moment.Moment | Date | string, options?: TimeFormatOptions) => string
) => {
  // will render a range if first stop of multiple trips span multiple days
  const orderedTrips = orderTripsByAscendingStopTimes(trips);
  const firstTrip = first(orderedTrips);
  const lastTrip = last(orderedTrips);

  const firstTripStartTime = moment.utc(first(firstTrip.stops).dateTime);
  const lastTripStartTime = moment.utc(first(lastTrip.stops).dateTime);
  const lastTripIsOnDifferentDayThanFirst = lastTripStartTime.isAfter(
    firstTripStartTime,
    "day"
  );

  const firstTripStartTimeDisplay = formatTime 
    ? formatTime(firstTripStartTime, { includeDate: true, dateFormat: "MMM Do YYYY" })
    : firstTripStartTime.format("MMM Do YYYY, h:mm A");
  const lastTripStartTimeDisplay = formatTime
    ? formatTime(lastTripStartTime, { includeDate: true, dateFormat: "MMM Do YYYY" })
    : lastTripStartTime.format("MMM Do YYYY, h:mm A");

  if (size(trips) === 1 || !lastTripIsOnDifferentDayThanFirst) {
    return { firstTripStartTimeDisplay };
  }

  return {
    firstTripStartTimeDisplay,
    lastTripStartTimeDisplay,
  };
};

export const isCharterTrips = (
  trips: { tripCategory?: TripCategory }[]
) => trips.some((trip) => trip.tripCategory === TripCategory.Charter);

export const formatCharterDateRangeTitle = (
  trips: {
    tripCategory?: TripCategory;
    charterServiceDate?: string;
    stops: { dateTime?: string }[];
  }[],
  fallbackTitle = ""
) => {
  const orderedTrips = orderTripsByAscendingStopTimes(trips);
  const charterStartDate = moment.utc(
    first(orderedTrips)?.charterServiceDate ||
      first(first(orderedTrips)?.stops)?.dateTime
  );
  const charterEndDate = moment.utc(
    last(orderedTrips)?.charterServiceDate ||
      first(last(orderedTrips)?.stops)?.dateTime
  );

  if (!charterStartDate.isValid() || !charterEndDate.isValid()) {
    return fallbackTitle;
  }

  if (charterStartDate.isSame(charterEndDate, "day")) {
    return `Charter: ${charterStartDate.format("MMM D, YYYY")}`;
  }

  if (
    charterStartDate.isSame(charterEndDate, "month") &&
    charterStartDate.isSame(charterEndDate, "year")
  ) {
    return `Charter: ${charterStartDate.format("MMM D")}–${charterEndDate.format(
      "D, YYYY"
    )}`;
  }

  if (charterStartDate.isSame(charterEndDate, "year")) {
    return `Charter: ${charterStartDate.format("MMM D")}–${charterEndDate.format(
      "MMM D, YYYY"
    )}`;
  }

  return `Charter: ${charterStartDate.format(
    "MMM D, YYYY"
  )}–${charterEndDate.format("MMM D, YYYY")}`;
};

export const decodeGlobalId = (globalId: string) => {
  const decodedString = atob(globalId);
  const delimiterIndex = decodedString.indexOf(":");

  return decodedString.substring(delimiterIndex + 1);
};
